import { TextButton } from "@/components/ui/pressable";
import { CircleX, Edit, Save, Trash2 } from "lucide-react";
import {
  Form,
  useDelete,
  useNotify,
  useResourceContext,
  useUpdate,
} from "ra-core";
import { useEffect, useRef, useState } from "react";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { ReferenceField } from "@/components/admin/reference-field";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { Markdown } from "../misc/Markdown";
import { RelativeDate } from "../misc/RelativeDate";
import { Status } from "../misc/Status";
import { parseAssetLinkText } from "../misc/assetLinks";
import { AuthorBadge } from "../accountability/AuthorBadge";
import { useMemberCapability } from "@/components/atomic-crm/providers/commons/useMemberCapability";
import { useIsAdminLevel } from "@/lib/permissions/useIsAdminLevel";
import { isPipelineTransitionNote } from "@/modules/leads/leadFollowUpUtils";
import type { ContactNote, DealNote } from "../types";
import { NoteAttachments } from "./NoteAttachments";
import { NoteInputs } from "./NoteInputs";

export const Note = ({
  showStatus,
  note,
  resource: resourceProp,
  compact = false,
}: {
  showStatus?: boolean;
  note: DealNote | ContactNote;
  isLast?: boolean;
  resource?: string;
  compact?: boolean;
}) => {
  const assetLink = parseAssetLinkText(note.text);
  const [isHover, setHover] = useState(false);
  const [isEditing, setEditing] = useState(false);
  const [isExpanded, setExpanded] = useState(false);
  const [isTruncated, setTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const resourceFromContext = useResourceContext();
  const resource = resourceProp ?? resourceFromContext ?? "contact_notes";
  const notify = useNotify();
  const isAdminLevel = useIsAdminLevel();
  const canEditNotes = useMemberCapability("crm.notes.edit");
  const canDeleteNotes = useMemberCapability("crm.notes.delete");
  const isSystemNote = isPipelineTransitionNote(note);
  const canEdit = canEditNotes && !isSystemNote;
  const canDelete = canDeleteNotes && !isSystemNote;

  // Detect if content is truncated
  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      setTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [note.text]);

  const [update, { isPending }] = useUpdate();

  const [deleteNote] = useDelete(resource, undefined, {
    mutationMode: "undoable",
    onSuccess: () => {
      notify("Note deleted", { type: "info", undoable: true });
    },
  });

  const handleDelete = () => {
    deleteNote(resource, { id: note.id, previousData: note });
  };

  const handleEnterEditMode = () => {
    setEditing(!isEditing);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setHover(false);
  };

  const handleNoteUpdate: SubmitHandler<FieldValues> = (values) => {
    update(
      resource,
      { id: note.id, data: values, previousData: note },
      {
        onSuccess: () => {
          setEditing(false);
          setHover(false);
        },
      },
    );
  };

  const content = (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={cn("group/note", compact ? "mb-0" : "mb-4")}
    >
      <div
        className={cn(
          "flex w-full items-center",
          compact ? "gap-2" : "space-x-4",
        )}
      >
        {compact ? null : (
          <ReferenceField source="company_id" reference="companies" link="show">
            <CompanyAvatar width={20} height={20} />
          </ReferenceField>
        )}
        <div
          className={cn(
            "inline-flex h-full items-center text-muted-foreground",
            compact ? "text-xs" : "text-sm",
          )}
        >
          {isAdminLevel ? (
            <>
              <AuthorBadge memberId={note.organization_member_id} /> added a
              note{" "}
            </>
          ) : (
            <>Note added </>
          )}
          {showStatus && note.status && (
            <Status className="ml-2" status={note.status} />
          )}
        </div>
        <span
          className={cn(
            "inline-flex items-center gap-0.5 transition-opacity",
            isHover
              ? "opacity-100"
              : "opacity-100 sm:opacity-0 sm:group-hover/note:opacity-100",
          )}
        >
          {canEdit ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEnterEditMode}
                    className="p-1 h-auto cursor-pointer"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Edit note</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
          {canDelete ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDelete}
                    className="p-1 h-auto cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete note</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </span>
        <div className="flex-1"></div>
        <span
          className={cn(
            "text-muted-foreground",
            compact ? "text-xs" : "text-sm",
          )}
        >
          <RelativeDate date={note.date} />
        </span>
      </div>
      {isEditing ? (
        <Form onSubmit={handleNoteUpdate} record={note} className="mt-1">
          <NoteInputs showStatus={showStatus} />
          <div className="flex justify-end mt-2 space-x-4">
            <Button
              variant="ghost"
              onClick={handleCancelEdit}
              type="button"
              className="cursor-pointer"
            >
              <CircleX className="w-4 h-4" />
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="flex items-center gap-2 cursor-pointer"
            >
              <Save className="w-4 h-4" />
              Update note
            </Button>
          </div>
        </Form>
      ) : (
        <div className={cn("text-sm max-w-150", compact ? "pt-1" : "pt-2")}>
          {assetLink ? (
            <div className="rounded-md border p-3">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">
                {assetLink.kind === "photos" ? "Photo Link" : "Document Link"}
              </div>
              <a
                href={assetLink.url}
                target="_blank"
                rel="noreferrer"
                className="block mt-1 text-sm font-medium underline"
              >
                {assetLink.title || assetLink.url}
              </a>
              {assetLink.note ? (
                <div className="mt-2 text-xs text-muted-foreground">
                  {assetLink.note}
                </div>
              ) : null}
            </div>
          ) : note.text ? (
            <div
              ref={contentRef}
              className={cn(
                "overflow-hidden transition-[max-height] duration-300 ease-in-out",
                isExpanded
                  ? "max-h-[5000px]"
                  : compact
                    ? "max-h-20"
                    : "max-h-46",
              )}
            >
              <Markdown
                className={cn(
                  "[&_p]:leading-5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:my-2 [&_blockquote]:text-muted-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:no-underline [&_ul]:list-disc [&_ul]:list-inside [&_ol]:list-decimal [&_ol]:list-inside",
                  compact ? "[&_p]:my-1" : "[&_p]:my-4",
                )}
              >
                {note.text}
              </Markdown>
            </div>
          ) : null}
          {!assetLink && isTruncated ? (
            <TextButton
              tone="accent"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!isExpanded);
              }}
              className="mt-1 hover:no-underline"
            >
              {isExpanded ? "Show less" : "Read more"}
            </TextButton>
          ) : null}

          {note.attachments && <NoteAttachments note={note} />}
        </div>
      )}
    </div>
  );

  return content;
};
